import * as React from 'react';
import {
  ContextualExpressions,
  type ContextualExpression,
  stripAnn,
  getAnn,
  stripAll,
  updateExpr,
  updateAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionBinding,
  ExpressionEditorDispatch,
  ExpressionEditorImplementationContext,
  extendExpressionEditorActionAtPath,
  updateExpressionAtAction,
  useExpressionEditorImplementationContext,
  getBindingType,
  getBindingIdAndPath,
} from '@packages/systems/ExpressionEditor-utils';
import {isCurrentPageNameBinding} from '@packages/systems/page-bindings';
import {GetPathForDispatch} from './types';
import {ExpressionEditor} from '@packages/systems/ExpressionEditor';
import {
  ExpressionPath,
  TypeCheckerState,
  Expressions,
  inferType,
  CheckerState,
  Types,
  Plugins,
} from '@packages/systems/wfdl';
import {usePlugins} from '@packages/utilities/platform';
import {BINDING_SELECT_BUTTON_HEIGHT} from '@packages/systems/ExpressionEditor-utils-ui';
import {
  findAvailableBindings,
  hasAvailableBindings,
  type AvailableBindings,
} from '@packages/systems/wfdl-findBindings';
import {EList, Expression} from '@packages/systems/wfdl-ast';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {emotionStyled, styled} from '@packages/utilities/styled';
import SelectButton from '@packages/systems/design-system/SelectButton';
import {bindChangedAt, withSlotContentOperationsEnabled} from './utils';
import {
  AddIcon,
  DeleteIcon,
  Button,
  IconButton,
  VStack,
  SelectTrigger,
} from '@packages/systems/spring';
import {buildNewCondition} from '@packages/systems/plugins-utils/Conditions';
import {deleteAt} from '@packages/utilities/array';
import {
  CustomBindingSelectButtonProps,
  CustomControls,
} from '@packages/systems/ExpressionEditor-custom-controls';
import {call as constructCall} from '@packages/utilities/wfdl-helpers';
import {filter, first, flatMap, map} from '@packages/utilities/lazy-list';
import {getFirst, getSecond} from '@packages/utilities/Pair';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {isOperatorAllowedForConditionField} from './operatorFiltering';
import {
  getAutoOpenConditionalPath,
  getAutoOpenConditionalGroupPath,
} from '@packages/systems/conditionals';
import {
  OperatorSelect,
  buildPredicateWithConditionValues,
} from './OperatorSelect';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {UTC} from '@packages/shared/utils/DateUtils';

const selectTimezone = (state: DesignerStores) =>
  state.DataManagerUIStore.timezone || UTC;

const PATH_TO_FIRST_GROUP: ExpressionPath = [
  {in: 'Call', at: 'arg'},
  {in: 'Record', at: 'rules'},
  {in: 'List', index: 0},
];

/**
 * Determines if a conditional should auto-open based on comparing expression paths
 * with auto-open conditional group and conditional paths.
 */
function getShouldAutoOpen(
  autoOpenConditionalGroupPath: ExpressionPath | null,
  autoOpenConditionalPath: ExpressionPath | null,
  currentExpressionPath: ExpressionPath
): boolean {
  let pathToAutoOpenGroup: ExpressionPath | null = null;

  if (autoOpenConditionalGroupPath) {
    // if we have a path to a specific group, use it
    pathToAutoOpenGroup = autoOpenConditionalGroupPath;
  } else if (autoOpenConditionalPath) {
    // we want to open condition source select for the first condition of
    // the first group of a newly created conditional
    // we only have the path to the conditional in element settings,
    // and we need to construct the path to the first group from that

    // this should be the index where the conditional call (or its wrapping
    // implicit call) is in the current expression path
    const conditionalIdx = autoOpenConditionalPath.length;

    // handle wrapping implicit function call around the conditional
    const hasWrappingImplicit =
      currentExpressionPath[conditionalIdx]?.in === 'Call' &&
      currentExpressionPath[conditionalIdx + 1]?.in === 'Call';

    pathToAutoOpenGroup = [...autoOpenConditionalPath];
    if (hasWrappingImplicit) {
      pathToAutoOpenGroup.push({in: 'Call', at: 'arg'});
    }
    pathToAutoOpenGroup.push(...PATH_TO_FIRST_GROUP);
  }

  if (pathToAutoOpenGroup) {
    return Expressions.pathsEqual(
      pathToAutoOpenGroup,
      currentExpressionPath.slice(0, -1)
    );
  }

  return false;
}

export function RuleConditions({
  expr,
  dispatch,
  getPathForDispatch,
}: {
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
  getPathForDispatch: GetPathForDispatch;
}) {
  const autoOpenConditionalGroupPath = useDesignerSelector(
    getAutoOpenConditionalGroupPath
  );
  const autoOpenConditionalPath = useDesignerSelector(
    getAutoOpenConditionalPath
  );

  const shouldAutoOpen = getShouldAutoOpen(
    autoOpenConditionalGroupPath,
    autoOpenConditionalPath,
    getAnn(expr).env.expressionPath
  );

  const {env} = useExpressionEditorImplementationContext();
  const [rootCheckerState] = React.useContext(TypeCheckerState);
  const callResult = ContextualExpressions.getCall(stripAnn(expr));
  const listExpr = callResult ? callResult.arg : null;
  const listResult = listExpr
    ? ContextualExpressions.getList(stripAnn(listExpr))
    : null;

  const deleteCondition = React.useCallback(
    (index: number) => {
      if (!listExpr) return;
      const strippedListExpr = stripAll(listExpr);
      const list = Expressions.getList(strippedListExpr);
      if (!list) return;
      const newList = deleteAt(list, index);
      const newListExpr = EList(newList);
      const path = getPathForDispatch(listExpr);
      dispatch(updateExpressionAtAction(newListExpr, path));
    },
    [listExpr, dispatch, getPathForDispatch]
  );

  const [
    newItemCheckerState,
    newItemContextualExpr,
    newItemOperandContextualExpr,
  ] = React.useMemo(() => {
    const newListItemExpr = buildNewCondition();
    const [_state, _contextualExpr] =
      inferType(newListItemExpr)(rootCheckerState);

    // Operand within Conditions::Apply predicate function call
    const pathToOperand: ExpressionPath = [
      {in: 'Call', at: 'fun'},
      {in: 'Call', at: 'arg'},
    ];
    const newItemOperand = ContextualExpressions.getIn(
      _contextualExpr,
      pathToOperand
    );

    return [_state, _contextualExpr, newItemOperand];
  }, [rootCheckerState]);

  if (!newItemOperandContextualExpr) {
    return null;
  }

  if (!listExpr || !listResult) {
    return null;
  }

  return (
    <VStack align="start">
      {listResult.map((condition, i) => (
        <RuleCondition
          key={i}
          expr={condition}
          dispatch={dispatch}
          getPathForDispatch={getPathForDispatch}
          onDelete={
            listResult.length > 1 ? () => deleteCondition(i) : undefined
          }
          newItemOperandContextualExpr={newItemOperandContextualExpr}
          newItemContextualExpr={newItemContextualExpr}
          newItemCheckerState={newItemCheckerState}
          shouldAutoOpen={shouldAutoOpen}
        />
      ))}
      {/* Both SelectField and SelectPredicate depend on the EE context being
          configured so that ExpressionBinding treats everything as bindable */}
      <ExpressionEditorImplementationContext.Provider
        value={{
          env: {...env, context: 'conditional-editor'},
        }}
      >
        <NewConditionButton
          conditionListExpr={listExpr}
          dispatch={dispatch}
          getPathForDispatch={getPathForDispatch}
          newItemOperandContextualExpr={newItemOperandContextualExpr}
          newItemContextualExpr={newItemContextualExpr}
          newItemCheckerState={newItemCheckerState}
        />
      </ExpressionEditorImplementationContext.Provider>
    </VStack>
  );
}

function NewConditionButton({
  conditionListExpr,
  dispatch,
  getPathForDispatch,
  newItemOperandContextualExpr,
  newItemContextualExpr,
  newItemCheckerState,
}: {
  conditionListExpr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
  getPathForDispatch: GetPathForDispatch;
  newItemOperandContextualExpr: ContextualExpression;
  newItemContextualExpr: ContextualExpression;
  newItemCheckerState: CheckerState;
}) {
  const customControls = React.useContext(CustomControls);
  const timezone = useDesignerSelector(selectTimezone);
  const plugins = usePlugins();
  const addCondition = React.useCallback(
    (operand: Expression | null) => {
      if (!operand) return;
      const strippedListExpr = stripAll(conditionListExpr);
      const list = Expressions.getList(strippedListExpr);
      if (!list) return;
      const newCondition = buildConditionWithFieldOperatorAndValue(
        operand,
        newItemCheckerState,
        plugins,
        timezone
      );
      const newConditionList = EList([...list, newCondition]);
      const path = getPathForDispatch(conditionListExpr);
      dispatch(
        bindChangedAt(
          newConditionList,
          getAnn(conditionListExpr).type,
          path,
          'conditional-source-selected'
        )
      );
    },
    [
      conditionListExpr,
      dispatch,
      getPathForDispatch,
      newItemCheckerState,
      plugins,
      timezone,
    ]
  );

  return (
    <ConditionWrapper>
      <TypeCheckerState.Provider
        value={[newItemCheckerState, newItemContextualExpr]}
      >
        <CustomControls.Provider
          value={{
            ...customControls,
            BindingTopLevelOptions: null,
          }}
        >
          <ExpressionBinding
            expression={newItemOperandContextualExpr}
            onChange={(binding) => {
              if (binding) {
                addCondition(binding);
              }
            }}
            button={(openPopover, targetRef) => (
              <span ref={targetRef}>
                <Hitarea>
                  <Button
                    onClick={openPopover}
                    variant="ghost"
                    colorVariant="secondary"
                    prefix={AddIcon}
                    justify="start"
                  >
                    New condition
                  </Button>
                </Hitarea>
              </span>
            )}
            bindUIVariant="custom"
          />
        </CustomControls.Provider>
      </TypeCheckerState.Provider>
    </ConditionWrapper>
  );
}

const FieldWrapper = emotionStyled('div')({
  gridColumn: 'field',
});

const OperatorWrapper = emotionStyled('div')({
  gridColumn: 'operator',
});

const ValueWrapper = emotionStyled('div')({
  gridColumn: 'value',
});

const DeleteButtonWrapper = emotionStyled('div')({
  gridColumn: 'delete-button',
  visibility: 'hidden',
});

const ConditionWrapper = emotionStyled('div')({
  display: 'grid',
  // the design calls for a narrower gap between value and delete button which
  // is why we define the gaps individually as columns in the template and then
  // set the gap between all columns to 0
  gridColumnGap: '0',
  gridTemplateColumns:
    '[field] 120px $2 [operator] 100px $2 [value] 164px $1 [delete-button] 24px',
  marginLeft: '$7',
  marginRight: '$2',

  [`&:hover ${DeleteButtonWrapper}`]: {
    visibility: 'visible',
  },
});

const StyledSelectButton = styled(SelectButton)({
  height: BINDING_SELECT_BUTTON_HEIGHT,
});

function RuleCondition({
  expr,
  dispatch,
  getPathForDispatch,
  onDelete,
  newItemOperandContextualExpr,
  newItemContextualExpr,
  newItemCheckerState,
  shouldAutoOpen,
}: {
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
  getPathForDispatch: GetPathForDispatch;
  onDelete?: () => void;
  newItemOperandContextualExpr: ContextualExpression;
  newItemContextualExpr: ContextualExpression;
  newItemCheckerState: CheckerState;
  shouldAutoOpen: boolean;
}) {
  const plugins = usePlugins();
  const [rootCheckerState, rootContextualExpr] =
    React.useContext(TypeCheckerState);
  const {env} = useExpressionEditorImplementationContext();
  const timezone = useDesignerSelector(selectTimezone);
  const call = ContextualExpressions.deconstructCall(expr);
  const [fieldArg, predicateArg] = call.args;

  // we want to build a new condition with the new source when the field is changed to a different type
  const changeConditionType = React.useCallback(
    (operand: Expression) => {
      if (operand) {
        dispatch(
          bindChangedAt(
            buildConditionWithFieldOperatorAndValue(
              operand,
              rootCheckerState,
              plugins,
              timezone
            ),
            getAnn(expr).type,
            getPathForDispatch(expr),
            'conditional-source-selected'
          )
        );
      }
    },
    [expr, dispatch, getPathForDispatch, rootCheckerState, plugins, timezone]
  );

  if (!fieldArg || !predicateArg) {
    return null;
  }

  const isFieldSelected = ContextualExpressions.isBinding(stripAnn(fieldArg));

  const fieldType = getBindingType(
    rootCheckerState.env.context.values,
    stripAll(fieldArg)
  );
  const isSlotContentOperand = Boolean(
    fieldType && Types.isSlotContentType(fieldType)
  );
  const operatorCheckerState = isSlotContentOperand
    ? withSlotContentOperationsEnabled(rootCheckerState)
    : rootCheckerState;
  const updatedPredicateArg = updateAnn((ann) => {
    const updatedEnv = operatorCheckerState.env;
    return {
      ...ann,
      env: {
        ...ann.env,
        context: {
          ...ann.env.context,
          values: updatedEnv.context.values,
        },
      },
    };
  }, predicateArg);

  return (
    <ConditionWrapper data-automation-id="rule-condition">
      {/* Both SelectField and SelectPredicate depend on the EE context being
          configured so that ExpressionBinding treats everything as bindable */}
      <ExpressionEditorImplementationContext.Provider
        value={{
          env: {...env, context: 'conditional-editor'},
        }}
      >
        <SelectField
          expr={fieldArg}
          dispatch={dispatch}
          getPathForDispatch={getPathForDispatch}
          onTypeChange={changeConditionType}
          newItemOperandContextualExpr={newItemOperandContextualExpr}
          newItemContextualExpr={newItemContextualExpr}
          newItemCheckerState={newItemCheckerState}
          shouldAutoOpen={shouldAutoOpen}
        />
        {isFieldSelected && (
          <OperatorWrapper>
            <TypeCheckerState.Provider
              value={[operatorCheckerState, rootContextualExpr]}
            >
              <OperatorSelect
                fieldExpr={fieldArg}
                expr={updatedPredicateArg}
                dispatch={dispatch}
                getPathForDispatch={getPathForDispatch}
              />
            </TypeCheckerState.Provider>
          </OperatorWrapper>
        )}
      </ExpressionEditorImplementationContext.Provider>
      {isFieldSelected && (
        <PredicateArguments
          expr={updatedPredicateArg}
          dispatch={dispatch}
          getPathForDispatch={getPathForDispatch}
          fieldExpr={fieldArg}
          predicateExpr={updatedPredicateArg}
        />
      )}
      {onDelete && (
        <DeleteButtonWrapper>
          <IconButton
            Icon={DeleteIcon}
            label="Delete condition"
            variant="ghost"
            onClick={onDelete}
          />
        </DeleteButtonWrapper>
      )}
    </ConditionWrapper>
  );
}

function SelectField({
  expr,
  dispatch,
  getPathForDispatch,
  onTypeChange,
  newItemOperandContextualExpr,
  newItemContextualExpr,
  newItemCheckerState,
  shouldAutoOpen,
}: {
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
  getPathForDispatch: (arg1: ContextualExpression) => ExpressionPath;
  onTypeChange: (arg: Expression) => void;
  newItemOperandContextualExpr: ContextualExpression;
  newItemContextualExpr: ContextualExpression;
  newItemCheckerState: CheckerState;
  shouldAutoOpen: boolean;
}) {
  // Used to control auto-opening the ExpressionBindingPopover if the Expression is a Hole (new condition source)
  const openRef = React.useRef<null | (() => void)>(null);
  React.useEffect(() => {
    if (shouldAutoOpen) {
      openRef.current?.();
    }
    openRef.current = null;
  }, [shouldAutoOpen]);
  const plugins = usePlugins();
  const customControls = React.useContext(CustomControls);

  const path = getPathForDispatch(expr);

  const dispatchBindingUpdate = (binding: null | Expression) => {
    // Note that we are not handling the case where the binding is null
    // as that should not be possible here because we are not providing the option
    // to unlink the binding at all.
    if (binding) {
      // We want to use the direct type of the given binding expression within
      // our checker environment.
      const originalBinding = stripAll(expr);
      const originalType = getBindingType(
        newItemCheckerState.env.context.values,
        originalBinding
      );
      const newType = getBindingType(
        newItemCheckerState.env.context.values,
        binding
      );
      // If binding is of the same type as original expression we can just change it.
      // Otherwise, we create a new condition with the new binding.
      if (originalType && newType && Types.equal(originalType, newType)) {
        const envValues = newItemCheckerState.env.context.values;
        const newFieldBinding = getBindingIdAndPath(envValues, binding);
        if (isCurrentPageNameBinding(newFieldBinding)) {
          // page.name restricts operators; rebuild to ensure a valid default
          onTypeChange(binding);
          return;
        }
        dispatch(
          bindChangedAt(
            binding,
            getAnn(expr).type,
            path,
            'conditional-source-selected'
          )
        );
        return;
      }
      onTypeChange(binding);
    }
  };

  if (ContextualExpressions.isHole(stripAnn(expr))) {
    const bindings = findAvailableBindings(expr, newItemCheckerState, plugins);

    const children = hasAvailableBindings(bindings) ? null : (
      <StyledSelectButton>No values to select</StyledSelectButton>
    );

    return (
      <FieldWrapper>
        <CustomControls.Provider
          value={{
            ...customControls,
            BindingTopLevelOptions: null,
          }}
        >
          <ExpressionBinding
            expression={expr}
            onChange={dispatchBindingUpdate}
            button={(openPopover, targetRef) => {
              openRef.current = () => openPopover({});
              return (
                <span ref={targetRef}>
                  <Hitarea>
                    <SelectFieldButton
                      label="Select field..."
                      automationLabel="Condition Field"
                      onClick={openPopover}
                    />
                  </Hitarea>
                </span>
              );
            }}
            bindUIVariant="custom"
            automationLabel="Condition Field"
          >
            {children}
          </ExpressionBinding>
        </CustomControls.Provider>
      </FieldWrapper>
    );
  }

  // Update expr to be typed as the default operand (hole), allows switching to any
  // applicable operand in scope.
  const strippedArg = stripAnn(expr);
  const updatedFieldArg = updateExpr(() => strippedArg)(
    newItemOperandContextualExpr
  );

  return (
    <FieldWrapper>
      <TypeCheckerState.Provider
        value={[newItemCheckerState, newItemContextualExpr]}
      >
        <CustomControls.Provider
          value={{
            ...customControls,
            CustomBindingSelectButton: SelectFieldButton,
            BindingTopLevelOptions: null,
            BindingDropdownButtonControls: null,
          }}
        >
          <ExpressionBinding
            expression={updatedFieldArg}
            onChange={dispatchBindingUpdate}
            bindUIVariant="dot"
            automationLabel="Condition Field"
            style={{
              // Override ExpressionBinding's default grid to allow text truncation
              // minWidth: 0 allows the container to shrink below its content width
              // gridTemplateColumns ensures the content can be constrained properly
              minWidth: 0,
              gridTemplateColumns: 'minmax(0, 1fr)',
            }}
          />
        </CustomControls.Provider>
      </TypeCheckerState.Provider>
    </FieldWrapper>
  );
}

function SelectFieldButton({
  label,
  automationLabel,
  pluginIcon,
  errorIcon,
  disabled,
  onClick,
}: CustomBindingSelectButtonProps) {
  return (
    <SelectTrigger
      data-automation-id={`binding-pill_${automationLabel}`}
      prefix={pluginIcon}
      suffix={errorIcon}
      isDisabled={disabled}
      onClick={onClick}
      shouldTruncateButtonText
      variant="outline"
    >
      {label}
    </SelectTrigger>
  );
}

function PredicateArguments({
  expr,
  dispatch,
  getPathForDispatch,
  fieldExpr,
  predicateExpr,
}: {
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
  getPathForDispatch: GetPathForDispatch;
  fieldExpr: ContextualExpression;
  predicateExpr: ContextualExpression;
}) {
  const [rootCheckerState] = React.useContext(TypeCheckerState);
  const customControls = React.useContext(CustomControls);
  const call = ContextualExpressions.deconstructCall(expr);

  // Pass conditional field and predicate expressions through context for property name generation
  const customTopLevelOptionsContext = React.useMemo(
    () => ({
      conditionalFieldExpr: fieldExpr,
      conditionalPredicateExpr: predicateExpr,
      conditionalEnvValues: rootCheckerState.env.context.values,
    }),
    [rootCheckerState, fieldExpr, predicateExpr]
  );

  if (call.args.length === 0) {
    return null;
  }

  return (
    <CustomControls.Provider
      value={{
        ...customControls,
        TopLevelOptionsContext: customTopLevelOptionsContext,
      }}
    >
      <ValueWrapper data-automation-id="predicate-arguments">
        {call.args.map((arg, i) => (
          <ExpressionEditor
            key={`${i}-call-predicate`}
            expression={arg}
            dispatch={extendExpressionEditorActionAtPath(dispatch)(
              getPathForDispatch(arg)
            )}
          />
        ))}
      </ValueWrapper>
    </CustomControls.Provider>
  );
}

/**
 * Returns a new condition expression with the given operand, a default operator
 * dependent on the type of the operand, and default arguments for the operator.
 */
function buildConditionWithFieldOperatorAndValue(
  operand: Expression,
  rootCheckerState: CheckerState,
  plugins: Plugins.PluginsType,
  timezone: string
) {
  // Check if operand is slot content type before creating the expression
  // so we can use the modified state for inferType
  const operandType = getBindingType(
    rootCheckerState.env.context.values,
    operand
  );
  const isSlotContentOperand = Boolean(
    operandType && Types.isSlotContentType(operandType)
  );
  const stateForInference = isSlotContentOperand
    ? withSlotContentOperationsEnabled(rootCheckerState)
    : rootCheckerState;

  const newCondition = buildNewCondition(operand);
  const [, newConditionWithHoleAtPredicate] =
    inferType(newCondition)(stateForInference);
  const call = ContextualExpressions.deconstructCall(
    newConditionWithHoleAtPredicate
  );
  const [fieldArg, predicateArg] = call.args;

  if (fieldArg && predicateArg) {
    const availableBindings = findAvailableBindings(
      predicateArg,
      stateForInference,
      plugins
    );
    // we assume that all predicate functions are provided by plugins
    const binding = findDefaultOperatorBinding(
      availableBindings,
      stripAll(fieldArg),
      rootCheckerState.env.context.values
    );

    if (binding) {
      // if the predicate is a call with arguments, make sure all arguments are
      // populated with default values
      const predicateWithConditionValues = buildPredicateWithConditionValues(
        binding,
        rootCheckerState,
        plugins,
        timezone
      );

      // return a new condition with the default operator and arguments
      return constructCall(stripAll(call.fun), [
        stripAll(fieldArg),
        predicateWithConditionValues,
      ]);
    }
  }

  // for some reason we could not determine a default operator, so return
  // a new condition with a hole in the predicate position
  return newCondition;
}

/**
 * Returns the first operator the field offers, skipping the ones the operator
 * select hides for that field.
 */
function findDefaultOperatorBinding(
  availableBindings: AvailableBindings,
  fieldExpr: Expression,
  envValues: CheckerState['env']['context']['values']
): Expression | null {
  const fieldBinding = getBindingIdAndPath(envValues, fieldExpr);

  const allowedBindings = filter(
    flatMap(availableBindings.plugins, (pluginEntry) =>
      map(getSecond(pluginEntry), (bindingEntry) => ({
        binding: getSecond(bindingEntry).expr,
        operatorName: qualify(getFirst(pluginEntry), getFirst(bindingEntry)),
      }))
    ),
    ({binding, operatorName}) =>
      Boolean(binding) &&
      (!fieldBinding ||
        isOperatorAllowedForConditionField({fieldBinding, operatorName}))
  );

  return first(allowedBindings)?.binding ?? null;
}
